import { Epic, User } from '../types';

export type LaneScope = 'active' | 'includeLater' | 'laterOnly';
export type TeamRole = 'owner' | 'architect';

export interface OwnerMetrics {
    userId: string;
    displayName: string;
    photoUrl?: string;
    // Load (Current)
    activeEpics: number;
    totalEffort: number;
    strategicPercent: number;
    // Effectiveness (Achieved)
    completedEpics: number;
    valueDelivered: number;
    efficiency: number;
    completionPercent: number; // Completed / (Active + Completed)
}

/**
 * Calculate performance metrics for each team member.
 * Groups epics by `so` (Service Owner) or `arq` (Architect) field.
 * 
 * @param role - Which field to group by: 'owner' (so) or 'architect' (arq)
 * @param scope - Which lanes to include:
 *   - 'active': Now + Next (immediate work)
 *   - 'includeLater': Now + Next + Later + Unassigned (full workload)
 *   - 'laterOnly': Later + Unassigned only (backlog analysis)
 */
export function calculateOwnerMetrics(
    epics: Epic[],
    users: User[],
    scope: LaneScope = 'active',
    role: TeamRole = 'owner'
): OwnerMetrics[] {
    const userMap = new Map(users.map(u => [u.id, u]));
    const metricsMap = new Map<string, {
        activeEpics: number;
        activeEffort: number;
        activeSgm: number;
        completedEpics: number;
        completedBusiness: number;
        completedEffort: number;
    }>();

    // Initialize all known users
    users.forEach(user => {
        metricsMap.set(user.id, {
            activeEpics: 0,
            activeEffort: 0,
            activeSgm: 0,
            completedEpics: 0,
            completedBusiness: 0,
            completedEffort: 0,
        });
    });

    // Process each epic
    epics.forEach(epic => {
        // Get the relevant people based on role toggle
        const field = role === 'owner' ? epic.so : epic.arq;
        const people = Array.isArray(field) ? field : (field ? [field] : []);

        // Determine if epic should be counted based on scope
        const isNowNext = epic.lane === 'Now' || epic.lane === 'Next';
        const isLaterOrUnassigned = epic.lane === 'Later' || epic.lane === 'Unassigned';
        const isDone = epic.lane === 'Done';

        let shouldCount = false;
        if (scope === 'active') {
            shouldCount = isNowNext;
        } else if (scope === 'includeLater') {
            shouldCount = isNowNext || isLaterOrUnassigned;
        } else if (scope === 'laterOnly') {
            shouldCount = isLaterOrUnassigned;
        }

        people.forEach(personId => {
            // ONLY process if this is a valid user ID from the database
            if (!userMap.has(personId)) {
                return; // Skip invalid/unknown user IDs
            }

            const data = metricsMap.get(personId)!;

            if (shouldCount) {
                data.activeEpics++;
                data.activeEffort += epic.effort || 0;
                if (epic.sgm) data.activeSgm++;
            }

            if (isDone) {
                data.completedEpics++;
                data.completedBusiness += epic.business || 0;
                data.completedEffort += epic.effort || 0;
            }
        });
    });

    // Convert to OwnerMetrics array
    const results: OwnerMetrics[] = [];

    metricsMap.forEach((data, userId) => {
        const user = userMap.get(userId);

        // Skip users with no activity at all
        if (data.activeEpics === 0 && data.completedEpics === 0) return;

        // Double-check user exists (should always be true now)
        if (!user) return;

        const strategicPercent = data.activeEpics > 0
            ? Math.round((data.activeSgm / data.activeEpics) * 100)
            : 0;

        const efficiency = data.completedEffort > 0
            ? Math.round((data.completedBusiness / data.completedEffort) * 10) / 10
            : 0;

        const totalEpics = data.activeEpics + data.completedEpics;
        const completionPercent = totalEpics > 0
            ? Math.round((data.completedEpics / totalEpics) * 100)
            : 0;

        results.push({
            userId,
            displayName: user.displayName || 'Unknown',
            photoUrl: user.photoUrl,
            activeEpics: data.activeEpics,
            totalEffort: data.activeEffort,
            strategicPercent,
            completedEpics: data.completedEpics,
            valueDelivered: data.completedBusiness,
            efficiency,
            completionPercent,
        });
    });

    // Sort by total activity (completed + active) descending
    return results.sort((a, b) =>
        (b.completedEpics + b.activeEpics) - (a.completedEpics + a.activeEpics)
    );
}

/**
 * Get load level indicator for visual styling.
 */
export function getLoadLevel(activeEpics: number): 'low' | 'balanced' | 'high' | 'critical' {
    if (activeEpics <= 2) return 'low';
    if (activeEpics <= 5) return 'balanced';
    if (activeEpics <= 8) return 'high';
    return 'critical';
}

/**
 * Calculate unique user counts for project roles
 * Only counts IDs that match valid users in the provided users list
 */
export function calculateProjectRoleCounts(
    epics: Epic[],
    project?: { pm?: string | string[] },
    users?: { id: string }[]
) {
    // Create a set of valid user IDs for quick lookup
    const validUserIds = new Set(users?.map(u => u.id) || []);

    // PMs come directly from project settings - only count valid user IDs
    const pms = new Set<string>();
    if (project?.pm) {
        const pmArray = Array.isArray(project.pm) ? project.pm : [project.pm];
        pmArray.forEach(id => {
            if (validUserIds.has(id)) {
                pms.add(id);
            }
        });
    }

    // Owners (SO) and Architects (Arq) come from unique assignments on Epics
    const owners = new Set<string>();
    const architects = new Set<string>();

    epics.forEach(epic => {
        // Count Owners - only if they match valid user IDs
        if (epic.so) {
            const soArray = Array.isArray(epic.so) ? epic.so : [epic.so];
            soArray.forEach(id => {
                if (validUserIds.has(id)) {
                    owners.add(id);
                }
            });
        }

        // Count Architects - only if they match valid user IDs
        if (epic.arq) {
            const arqArray = Array.isArray(epic.arq) ? epic.arq as string[] : [epic.arq];
            arqArray.forEach(id => {
                if (validUserIds.has(id)) {
                    architects.add(id);
                }
            });
        }
    });

    return {
        pmCount: pms.size,
        ownerCount: owners.size,
        architectCount: architects.size
    };
}
